import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_ITEMS } from "@/config/navigation";

/* ── Route groups per tab ── */

const RECIPES_ROUTES: string[] = [
  "/recipes",
  "/reposteria-cero",
  "/create-recipe",
  "/my-recipes",
  "/coleccion",
  "/product",
];

const MORE_ROUTES: string[] = [
  "/more",
  "/profile",
  "/settings",
  "/feedback",
  "/shopping-list",
  "/saved-recipes",
  "/support",
  "/help",
  "/rate",
  "/my-content",
  "/clean-remedies",
];

const GLUCOPHOTO_ROUTES: string[] = ["/glucophoto"];

/** Check whether the current path belongs to a tab's route group */
const matchesGroup = (pathname: string, routes: string[]): boolean =>
  routes.some((r) => pathname === r || pathname.startsWith(r + "/"));

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const p = location.pathname;
  const isAdmin = user?.email === 'admin@lamp.com';

  // Hide only on cooking mode and login
  const isCooking = p.endsWith("/cooking");
  const isLogin = p === "/login";
  const isFeedbackThread = /^\/feedback\/[^/]+$/.test(p) && p !== "/feedback";
  if (isCooking || isLogin || isFeedbackThread) return null;

  const isActiveItem = (key: string) => {
    const isInicio    = p === "/" || p === "/home";
    const isRecetas   = matchesGroup(p, RECIPES_ROUTES);
    const isGlucoPhoto = matchesGroup(p, GLUCOPHOTO_ROUTES);
    const isMais      = matchesGroup(p, MORE_ROUTES);
    switch (key) {
      case "home":       return isInicio;
      case "recipes":    return isRecetas;
      case "glucophoto": return isGlucoPhoto;
      case "more":       return isMais;
      default:           return false;
    }
  };

  const tabs = NAV_ITEMS.map((item) => ({
    ...item,
    active: isActiveItem(item.key),
    label: item.i18nKey ? t(item.i18nKey, item.label) : item.label,
    // BottomNav routes to "/" for home; override path accordingly
    path: item.key === "home" ? "/" : item.path,
  }));

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 safe-bottom md:hidden">
      <div className="max-w-[520px] mx-auto">
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            backgroundColor: "#F2EBD9",
            borderTop: "0.5px solid #E0D4C0",
            paddingTop: "12px",
            paddingBottom: "12px",
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const activeColor = tab.accentColor;
            const iconColor = tab.active ? activeColor : "#8C7355";
            return (
              <button
                data-onboarding={tab.key}
                key={tab.key}
                onClick={() => navigate(tab.path)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  flex: 1,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  transition: "opacity 0.15s",
                }}
              >
                <div style={{ transform: tab.active ? "scale(1.1)" : "scale(1)", transition: "transform 0.2s cubic-bezier(0.34,1.56,0.64,1)" }}>
                  <Icon
                    size={24}
                    strokeWidth={tab.active ? 1.8 : 1.5}
                    style={{ color: iconColor }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 12,
                    lineHeight: 1.2,
                    color: iconColor,
                    fontWeight: tab.active ? 500 : 400,
                  }}
                >
                  {tab.label}
                </span>
                {/* Active dot */}
                <div
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: tab.active ? activeColor : "transparent",
                    marginTop: 1,
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
